import { ArrowPathIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Button } from '~/components';
import { b2bSideCopy } from '../../b2bSideCopy';
import { FIT_QUOTA_MESSAGE } from '../../listings/listingConflicts';
import {
  fitComponentRows,
  fitPercentLabel,
  percentOf,
  sellerRadiusMiles,
  weightLabel,
} from '../offeringFit';
import type { MsaasJobListingResponse } from '../types';

// [MCV-1889] The Buying tab's fit area (DD10). A listing there carries its own fit, scored against
// the buyer's chosen listing, so these read the search result instead of the scores endpoint.

type OfferingFitProps = {
  listing: MsaasJobListingResponse;
  // Title of the listing the fit was compared against.
  compareTitle?: string;
  // FTEs on that listing, for the "roster too small" message.
  compareFtes?: number | null;
  // Whether the buyer has any active listing to compare against.
  canCompare: boolean;
  // A 429 turned fit checks off until midnight UTC.
  quotaActive: boolean;
  onCheckFit: () => void;
  // Where "Post what you need to compare fit" goes: the buying-labor wizard.
  postNeedHref: string;
};

const QUOTA_MESSAGE = FIT_QUOTA_MESSAGE;

export const OfferingFitRow = ({
  listing,
  compareTitle,
  quotaActive,
  onCheckFit,
  align = 'right',
}: Pick<OfferingFitProps, 'listing' | 'compareTitle' | 'quotaActive' | 'onCheckFit'> & {
  // The result card's fit slot reads left to right, like the Selling card's match block.
  align?: 'left' | 'right';
}) => {
  const status = listing.fit?.status ?? 'NO_COMPARE_LISTING';
  const percent = fitPercentLabel(listing.fit);
  return (
    <div
      className={`shrink-0 text-sm leading-5 ${align === 'left' ? 'text-left' : 'text-right'}`}
      data-cy="offering-fit-row"
    >
      {status === 'COMPLETED' && percent ? (
        <>
          <p className="text-lg font-bold leading-6 text-primary-50">{percent}</p>
          {compareTitle && (
            <p className="max-w-[9rem] truncate text-content-secondary" title={compareTitle}>
              vs {compareTitle}
            </p>
          )}
        </>
      ) : status === 'NOT_SCORED' ? (
        <Button
          type="button"
          colorVariant="tertiary"
          label="Check fit"
          disabled={quotaActive}
          onClick={onCheckFit}
        />
      ) : status === 'PENDING' ? (
        <p className="text-content-secondary">Checking fit…</p>
      ) : status === 'FAILED' ? (
        <p className="text-content-secondary">Couldn't check fit</p>
      ) : status === 'BELOW_MIN_PROVIDERS' ? (
        <p className="text-content-secondary">Roster too small</p>
      ) : status === 'NO_ROSTER' ? (
        <p className="text-content-secondary">Roster on request</p>
      ) : status === 'NO_ELIGIBLE_PROVIDERS' ? (
        <p className="text-content-secondary">No eligible providers</p>
      ) : null}
    </div>
  );
};

const BreakdownRow = ({
  label,
  score,
  weight,
  detail,
}: {
  label: string;
  score: number | null;
  weight: number;
  detail?: string;
}) => (
  <li className="flex items-baseline justify-between gap-3 py-1">
    <span className="min-w-0">
      <span className="font-semibold text-content-primary">{label}</span>
      {detail && <span className="block text-content-secondary">{detail}</span>}
    </span>
    <span className="shrink-0 text-right">
      <span className="font-semibold text-content-primary">{percentOf(score) ?? '—'}</span>
      <span className="block text-content-secondary">{weightLabel(weight)}</span>
    </span>
  </li>
);

const FitBreakdown = ({ listing }: { listing: MsaasJobListingResponse }) => {
  const fit = listing.fit;
  if (!fit) return null;
  const radius = sellerRadiusMiles(listing);
  const distance = fit.distanceMiles != null ? Math.round(fit.distanceMiles) : null;
  const adjustments = [
    fit.components.penaltyApplied
      ? `−20 pts: outside travel range${distance != null ? ` (${distance} mi)` : ''}`
      : null,
    fit.components.proximityCapped
      ? `Proximity capped: no providers within ${radius ?? 'the seller’s'} mi`
      : null,
  ].filter((line): line is string => line != null);
  return (
    <div data-cy="offering-fit-breakdown">
      <h4 className="mt-3 text-sm font-semibold text-content-primary">Fit breakdown</h4>
      <ul className="mt-1 divide-y divide-grey-900 text-sm leading-5">
        {fitComponentRows(listing).map(({ key, ...row }) => (
          <BreakdownRow key={key} {...row} />
        ))}
      </ul>
      {adjustments.length > 0 && (
        <ul className="mt-2 text-sm leading-5 text-warning-950">
          {adjustments.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const StatusLine = ({ children }: { children: ReactNode }) => (
  <p className="text-sm leading-6 text-content-secondary">{children}</p>
);

export const OfferingFitDetail = ({
  listing,
  compareTitle,
  compareFtes,
  canCompare,
  quotaActive,
  onCheckFit,
  postNeedHref,
}: OfferingFitProps) => {
  const status = listing.fit?.status ?? 'NO_COMPARE_LISTING';
  const percent = fitPercentLabel(listing.fit);
  return (
    <div className="my-3" data-cy="offering-fit-detail">
      <h3 className="text-sm font-semibold leading-6 text-content-primary">Fit</h3>
      {/* The result of "Check fit" arrives later, so assistive tech hears it as it changes. */}
      <div role="status" aria-live="polite">
        {status === 'NO_COMPARE_LISTING' && !canCompare && (
          <StatusLine>
            <Link className="underline" to={postNeedHref}>
              Post what you need to compare fit
            </Link>
          </StatusLine>
        )}
        {status === 'NO_COMPARE_LISTING' && canCompare && (
          <StatusLine>Choose “Compare to my listing” to see how this roster fits.</StatusLine>
        )}
        {status === 'NO_ROSTER' && (
          <StatusLine>{b2bSideCopy('OFFERING').emptyRosterNote}</StatusLine>
        )}
        {status === 'BELOW_MIN_PROVIDERS' && (
          <StatusLine>
            {compareFtes != null
              ? `Roster too small for your ${compareFtes}-FTE listing`
              : 'Roster too small for your listing'}
          </StatusLine>
        )}
        {status === 'NO_ELIGIBLE_PROVIDERS' && (
          <StatusLine>No providers meet your listing's requirements</StatusLine>
        )}
        {status === 'PENDING' && (
          <p className="flex items-center gap-2 text-sm leading-6 text-content-secondary">
            <ArrowPathIcon className="size-4 animate-spin" aria-hidden="true" />
            Checking fit…
          </p>
        )}
        {status === 'FAILED' && <StatusLine>Couldn't check fit</StatusLine>}
        {status === 'COMPLETED' && percent && (
          <p className="text-sm leading-6 text-content-secondary">
            <span className="text-lg font-bold text-primary-50">{percent}</span>
            {compareTitle ? ` vs ${compareTitle}` : ''}
          </p>
        )}
        {status === 'NOT_SCORED' && quotaActive && <StatusLine>{QUOTA_MESSAGE}</StatusLine>}
      </div>
      {(status === 'NOT_SCORED' || status === 'PENDING' || status === 'FAILED') && (
        <div className="mt-2">
          <Button
            type="button"
            colorVariant="secondary"
            label={status === 'FAILED' ? 'Retry' : 'Check fit'}
            disabled={status === 'PENDING' || quotaActive}
            onClick={onCheckFit}
          />
        </div>
      )}
      {status === 'COMPLETED' && <FitBreakdown listing={listing} />}
    </div>
  );
};
